import * as React from 'react'
import { useState } from 'react'
import { Outlet } from 'react-router-dom'

import { Box, Section } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { isManager } from 'modules/employees/lib/isManager'
import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import { launchSurveys } from 'modules/engagement/lib/authorization'
import InsightsBanner from 'modules/engagement_insights/components/InsightsBanner'
import ManagerSelector, { Manager } from 'modules/engagement_insights/components/ManagerSelector'
import { EngagementInsightsProvider } from 'modules/engagement_insights/hooks/useEngagementInsightsData'
import getCurrentClimate from 'modules/engagement_insights/lib/getCurrentClimate'
import EmptyPage from 'modules/engagement_insights/pages/Insights/Empty'
import StatsPage from 'modules/engagement_insights/pages/Insights/Stats'
import enpsForm from 'modules/enps/store/collections/forms'
import type EnpsForm from 'modules/enps/store/models/Form'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { canEditSurvey } from 'modules/surveys/lib/canEditSurvey'
import type Survey from 'modules/surveys/store/models/v2/Survey'

const IndexPage = () => {
  const {
    access: { fullName, avatar },
    employee,
  } = useCurrentSession()
  const WAIT_FOR_TRACKING = 5000

  const { moduleAvailabilities, enpsInstalled, surveysInstalled, canManageSurveys } =
    useEngagementContext()
  const [climate, setClimate] = React.useState<Survey>()
  const [surveyToBeLaunched, setSurveyToBeLaunched] = React.useState<number>()
  const [selectedManagerId, setSelectedManagerId] = useState<string>('')

  const canLaunchSurveys = launchSurveys(moduleAvailabilities, canManageSurveys)

  const fetchClimate = async () => {
    const survey = await getCurrentClimate()
    setClimate(survey)
  }

  const fetchSurveys = async () => Promise.all([fetchClimate(), enpsForm.fetch()])
  const { loading } = useAsyncData(() => fetchSurveys(), [])

  const enps = enpsForm.models[0] as EnpsForm

  const userCanEditSurvey = !!climate ? canEditSurvey(climate, canManageSurveys) : false
  const managedSurvey = !userCanEditSurvey && isManager(employee)
  const managerId = selectedManagerId
    ? parseInt(selectedManagerId)
    : managedSurvey
    ? employee?.id
    : undefined
  const canSeeAllSurveys = canManageSurveys
  const isAdminAndManager = canSeeAllSurveys && isManager(employee)
  const canSeeTurnoverWidget = isAdminAndManager ? !selectedManagerId : canSeeAllSurveys

  const climateId = climate?.attributes.id

  const enpsIsDraft = enps?.isDraft && !enps?.hasSchedule
  const enpsIsScheduled = enps?.hasSchedule && !enps?.isActive && !enps?.isFinished
  const enpsIsActive = enps?.isActive && !enpsIsScheduled
  const enpsInactive = !enpsInstalled || !enps || enpsIsDraft
  const climateInactive = !surveysInstalled || !climate || climate?.isDraft

  const enpsStatus = enps?.attributes?.state
  const climateStatus = climate?.attributes?.status

  const emptyPage = enpsInactive && climateInactive

  const contextValues = {
    climate,
    climateId,
    climateInactive,
    climateStatus,
    emptyPage,
    enps,
    enpsInactive,
    enpsInstalled,
    enpsIsActive,
    enpsIsDraft,
    enpsIsScheduled,
    enpsStatus,
    fetchSurveys,
    managerId,
    canSeeTurnoverWidget,
    setSurveyToBeLaunched,
    surveysInstalled,
    surveyToBeLaunched,
  }

  const handleEmployeeChange = (value: string | undefined) => {
    setSelectedManagerId(value || '')
  }

  const showInsightsBanner =
    enps?.isScheduled ||
    climate?.isScheduled ||
    (enps?.isScheduled && climate?.isScheduled) ||
    climate?.isFinished ||
    enps?.isFinished ||
    (climate?.isFinished && enps?.isFinished)

  const filteredManagers: Manager[] = employee
    ? [
        {
          fullName: fullName,
          id: employee.id,
          access: {
            avatar: avatar ?? '',
          },
        },
      ]
    : []

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!loading) {
        track('Insights dashboard view', {
          enps_status: enpsStatus,
          climate_status: climateStatus,
        })
      }
    }, WAIT_FOR_TRACKING)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [loading, enpsStatus, climateStatus])

  return (
    <ResponsivePage.Body loading={loading} showHeader={false} error={null}>
      <EngagementInsightsProvider data={contextValues}>
        {showInsightsBanner && <InsightsBanner />}
        <Section.FullWidth
          illustration="group"
          title={
            canLaunchSurveys
              ? i18n.t('engagement_insights.admin.title')
              : i18n.t('engagement_insights.manager.title')
          }
          description={i18n.t('engagement_insights.description')}
          size="600"
        >
          <Box width="full" gap="s32">
            {isAdminAndManager && (
              <ManagerSelector
                value={selectedManagerId}
                onChange={handleEmployeeChange}
                managers={filteredManagers}
              />
            )}

            {!!emptyPage ? <EmptyPage /> : <StatsPage />}

            <Outlet />
          </Box>
        </Section.FullWidth>
      </EngagementInsightsProvider>
    </ResponsivePage.Body>
  )
}

export default observer(IndexPage)
